<script setup>
import { useBreakpoints } from "@vueuse/core";
import { differenceInDays, format, isBefore, isSameMonth } from "date-fns";
import mentor1 from "~/assets/img/mentor-profiles/mentor1.jpg";
import mentor2 from "~/assets/img/mentor-profiles/mentor2.jpg";
import mentor3 from "~/assets/img/mentor-profiles/mentor3.jpg";
import mentor4 from "~/assets/img/mentor-profiles/mentor4.jpg";
import mentor5 from "~/assets/img/mentor-profiles/mentor5.jpg";

definePageMeta({
  layout: "empty",
  auth: false,
});

const items = ref([
  { icon: "pi-wallet", title: "Consultant fees", value: "FREE" },
  { icon: "pi-map-marker", title: "Location", value: "Online" },
  { icon: "pi-user", title: "Counsellor", value: "11,276" },
  { icon: "pi-flag", title: "From country", value: "+13" },
]);

const breakpoints = useBreakpoints(BREAKPOINTS);
const activeBreakpoint = breakpoints.active();
const isMobile = breakpoints.smaller("laptop");

const slidesPerView = computed(() => {
  switch (activeBreakpoint.value) {
    case "mobile":
      return 1;
    case "tablet":
      return 1.5;
    case "laptop":
      return 2.5;
    case "desktop":
      return 2.5;
    default:
      return 1;
  }
});

const sidebarItems = ref([
  "Program",
  "Key information",
  "University",
  "Program structure",
  "Tuition fees",
  // "Accommodation",
  "Admissions",
  // "Professor",
  "Possible Career Pathway",
  "Student's testimonials",
  "F&Qs",
]);

const activeSidebar = ref("Program");
const expandUniAbout = ref(false);

const nearestIntakeIndex = ref(-1);

const route = useRoute();

const { data: exploreCount } = usePublicFetch("explore-count", {
  method: "GET",
  key: "explore-count",
});

const { data: program } = await usePublicFetch(
  () => `/programs/${route.params.programSlug}`,
  {
    key: "program-detail-by-slug",
    transform(program) {
      return program?.data;
    },
  },
);

function selectSidebar(name) {
  activeSidebar.value = name;

  const target = document.getElementById(name);
  if (target) {
    target.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }
}

const universityImages = computed(() => {
  return (
    program?.value?.university?.media
      ?.filter((media) => media.collection_name === "university_images")
      ?.map((media) => ({
        itemImageSrc: media?.original_url,
        thumbnailImageSrc: media?.original_url,
        alt: program?.value?.university?.name,
      })) || []
  );
});

function formatDate(inputDate, currentDate = new Date()) {
  // if (isSameMonth(inputDate, currentDate)) {
  //   const daysLeft = differenceInDays(
  //     new Date(inputDate.setHours(0, 0, 0, 0)),
  //     new Date(currentDate.setHours(0, 0, 0, 0)),
  //   );

  //   if (daysLeft < 0) {
  //     return format(inputDate, "dd MMM yyyy");
  //   }

  //   return `${daysLeft} day(s) left`;
  // } else if (isBefore(inputDate, currentDate)) {
  //   return "Deadline over";
  // }
  if (isBefore(inputDate, currentDate)) {
    return "Deadline over";
  }

  return format(inputDate, "MMM yyyy");
}

const nearestEndDate = computed(() => {
  if (route?.query?.end_date) {
    const endDate = new Date(route.query.end_date);
    const now = new Date();
    endDate.setFullYear(now.getFullYear());
    if (endDate < now) {
      endDate.setFullYear(now.getFullYear() + 1);
    }
    return endDate;
  }

  const intakes = program?.value?.intakes?.data || [];
  const now = new Date();

  // Find nearest future intake by comparing end dates
  let nearestIndex = -1;
  let nearestDate = null;

  intakes.forEach((intake, index) => {
    const endDate = new Date(intake.end_date);
    endDate.setFullYear(now.getFullYear());

    if (endDate < now) {
      endDate.setFullYear(now.getFullYear() + 1);
    }

    if (!nearestDate || endDate < nearestDate) {
      nearestDate = endDate;
      nearestIndex = index;
    }
  });

  // Store index for getting corresponding start date
  if (nearestIndex !== -1) {
    nearestIntakeIndex.value = nearestIndex;
  }

  return nearestDate || new Date();
});

const nearestStartDate = computed(() => {
  if (route?.query?.start_date) {
    const start_date = route?.query?.start_date;
    const startDate = new Date(start_date);
    const now = new Date();
    startDate.setFullYear(now.getFullYear());
    if (startDate < now) {
      startDate.setFullYear(now.getFullYear() + 1);
    }
    return startDate;
  }

  if (nearestIntakeIndex.value !== -1) {
    const intake = program?.value?.intakes?.data[nearestIntakeIndex.value];
    if (intake) {
      const startDate = new Date(intake.start_date);
      const endDate = new Date(intake.end_date);
      const now = new Date();

      endDate.setFullYear(now.getFullYear());
      startDate.setFullYear(now.getFullYear());

      if (endDate < now) {
        startDate.setFullYear(now.getFullYear() + 1);
      }

      return startDate;
    }
  }

  return new Date();
});

const getTestimonialImage = (media) => {
  if (!media || !Array.isArray(media)) return null;

  const testimonialImage = media.find(
    (item) => item?.collection_name === "student_testimonial_images",
  );
  return testimonialImage?.original_url || null;
};

function getAttributeData(key) {
  const attribute = program?.value?.attributes?.data?.find(
    (d) => d.key === key,
  );
  return attribute.value;
}

const mentorImages = [mentor1, mentor2, mentor3, mentor4, mentor5];

// --- SEO ---
useHead({
  link: [
    {
      rel: "canonical",
      href: `https://unilinks.co/programs/${route.params.programSlug}`,
    },
  ],
});

useSeoMeta({
  title: `Unilinks - ${program.value?.name}`,
  ogTitle: `Unilinks - ${program.value?.name}`,
  description:
    "Facing university hurdles? Our program consultation is here to help you conquer them",
  ogDescription:
    "Facing university hurdles? Our program consultation is here to help you conquer them",
  ogImage: META_IMAGE_URL,
  ogUrl: `https://unilinks.co/programs/${route.params.programSlug}`,
  twitterCard: "summary_large_image",
  twitterTitle: `Unilinks - ${program.value?.name}`,
  twitterDescription:
    "Facing university hurdles? Our program consultation is here to help you conquer them",
  twitterImage: META_IMAGE_URL,
});
</script>

<template>
  <NuxtLayout name="primary">
    <div class="w-full">
      <div class="mx-auto w-full max-w-7xl px-4 lg:px-4 mb-12 mt-6">
        <div
          class="my-3 flex flex-col md:flex-row items-start md:items-center justify-between"
        >
          <div class="w-full">
            <h1 class="text-primary-500 font-bold text-2xl md:text-3xl">
              {{ program?.name }}
            </h1>
            <div class="flex flex-wrap items-center gap-4 mt-2">
              <!-- University Logo and Name -->
              <div class="flex items-center gap-3 order-1 md:order-2">
                <NuxtLink :to="`/universities/${program?.university?.slug}`">
                  <ProfileAvatar
                    :imageSrc="getUniversityLogo(program?.university?.media)"
                    :name="program?.university?.name"
                    :size="isMobile ? 'sm' : 'md'"
                    className="hover:underline"
                  />
                </NuxtLink>
              </div>

              <!-- Degree Info -->
              <div class="flex items-center gap-3 order-2 md:order-1">
                <i class="text-md pi pi-graduation-cap"></i>
                <p class="text-sm lg:text-base">
                  {{
                    program?.attributes?.data?.find(
                      (att) => att?.key === "degree",
                    )?.value || ""
                  }}
                  Degree
                </p>
              </div>
            </div>
          </div>
          <div
            class="w-full md:w-1/2 flex items-center gap-2 md:justify-end md:mt-2 mt-4"
          >
            <CompareBtn
              :id="route.params.programSlug"
              :slug="route.params.programSlug"
              :isToggle="true"
            />
            <WishlistBtn :id="program?.id" />
          </div>
        </div>

        <!-- class="bg-center bg-no-repeat bg-cover"
          :style="{ backgroundImage: `url(${backgroundUrl})` }" -->

        <div class="my-8 w-full h-full rounded-lg bg-slate-300 relative">
          <Galleria
            v-if="universityImages?.length > 0"
            :value="universityImages"
            :numVisible="5"
            :circular="true"
            :showThumbnails="false"
            :autoPlay="true"
            :transitionInterval="3000"
            :pt="{
              root: 'border border-white overflow-hidden rounded-md',
            }"
          >
            <template #item="slotProps">
              <img
                :src="slotProps.item.itemImageSrc"
                :alt="slotProps.item.alt"
                style="width: 100%; display: block; max-height: 560px"
                class="h-[200px] lg:h-[560px] w-full object-cover"
              />
            </template>
            <template #thumbnail="slotProps">
              <img
                :src="slotProps.item.thumbnailImageSrc"
                :alt="slotProps.item.alt"
                class="h-20 w-full object-cover"
              />
            </template>
          </Galleria>
          <div
            v-else
            class="h-[200px] lg:h-[560px] bg-slate-200 border border-white rounded-md"
          ></div>
          <div
            class="hidden md:block w-[440px] absolute top-12 right-12 space-y-8 py-10 px-8 bg-primary-600 rounded-xl"
          >
            <div class="">
              <p class="text-white font-bold text-xl">
                Free consultation for this program
              </p>
              <p class="text-sm text-[#EFF3F8] text-normal">
                We are ready to help you with your difficulties and processing.
              </p>
            </div>

            <div class="grid grid-cols-2 gap-4 text-[#EFF6FF]">
              <div
                class="flex items-start flex-nowrap p-2 rounded-xl gap-4 bg-[#496c9566]"
              >
                <i class="pi pi-wallet text-xl mt-1"></i>
                <div class="space-y-1.5">
                  <p class="text-sm font-normal">Duration</p>
                  <p class="text-[18px] font-bold">
                    {{ getAttributeData("duration") }}
                  </p>
                </div>
              </div>
              <div
                class="flex items-start flex-nowrap p-2 rounded-xl gap-4 bg-[#496c9566]"
              >
                <i class="pi pi-flag text-xl mt-1"></i>
                <div class="space-y-1.5">
                  <p class="text-sm font-normal">Study Format</p>
                  <p class="text-[18px] font-bold">
                    {{ getAttributeData("study_format") }}
                  </p>
                </div>
              </div>
              <div
                class="flex items-start flex-nowrap p-2 rounded-xl gap-4 bg-[#496c9566]"
              >
                <i class="pi pi-user text-xl mt-1"></i>
                <div class="space-y-1.5">
                  <p class="text-sm font-normal">No. of Student Mentors</p>
                  <p class="text-[18px] font-bold">
                    {{ numberFormatter.format(exploreCount?.data?.mentors) }}
                  </p>
                </div>
              </div>
              <div
                class="flex items-start flex-nowrap p-2 rounded-xl gap-4 bg-[#496c9566]"
              >
                <i class="pi pi-wallet text-xl mt-1"></i>
                <div class="space-y-1.5">
                  <p class="text-sm font-normal">Total Tuition Fees</p>
                  <p class="text-[18px] font-bold">
                    {{ getTuitionFee(program?.tuition_fee) }}
                  </p>
                </div>
              </div>
            </div>
            <div class="space-y-4">
              <ChooseSchedule :program="program" />
              <div class="flex items-center space-x-[-12px]">
                <div
                  v-for="(img, index) in mentorImages"
                  :key="index"
                  class="w-10 h-10 rounded-full border-2 border-white overflow-hidden"
                >
                  <img
                    :src="img"
                    alt="Mentor"
                    class="w-full h-full object-cover"
                  />
                </div>
                <div
                  class="w-10 h-10 rounded-full border-2 border-white bg-white flex items-center justify-center text-sm font-medium text-blue-700"
                >
                  +48
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- mobile schedule -->
        <div
          class="block md:hidden col-span-12 py-8 px-4 sm:px-6 bg-primary-600 rounded-xl"
        >
          <div class="">
            <p class="text-white font-bold text-xl">
              Free consultation for this program
            </p>
            <p class="text-sm text-[#EFF3F8] text-normal">
              We are ready to help you with your difficulties and processing.
            </p>
          </div>

          <div class="grid grid-cols-1 my-5 gap-4 text-[#EFF6FF]">
            <div
              class="flex items-start flex-nowrap p-2 rounded-xl gap-4 bg-[#496c9566]"
            >
              <i class="pi pi-wallet text-xl mt-1"></i>
              <div class="space-y-1.5">
                <p class="text-sm font-normal">Duration</p>
                <p class="text-[18px] font-bold">
                  {{ getAttributeData("duration") }}
                </p>
              </div>
            </div>
            <div
              class="flex items-start flex-nowrap p-2 rounded-xl gap-4 bg-[#496c9566]"
            >
              <i class="pi pi-flag text-xl mt-1"></i>
              <div class="space-y-1.5">
                <p class="text-sm font-normal">Study Format</p>
                <p class="text-[18px] font-bold">
                  {{ getAttributeData("study_format") }}
                </p>
              </div>
            </div>
            <div
              class="flex items-start flex-nowrap p-2 rounded-xl gap-4 bg-[#496c9566]"
            >
              <i class="pi pi-user text-xl mt-1"></i>
              <div class="space-y-1.5">
                <p class="text-sm font-normal">No. of Student Mentors</p>
                <p class="text-[18px] font-bold">
                  {{ numberFormatter.format(exploreCount?.data?.mentors) }}
                </p>
              </div>
            </div>
            <div
              class="flex items-start flex-nowrap p-2 rounded-xl gap-4 bg-[#496c9566]"
            >
              <i class="pi pi-wallet text-xl mt-1"></i>
              <div class="space-y-1.5">
                <p class="text-sm font-normal">Total Tuition Fees</p>
                <p class="text-[18px] font-bold">
                  {{ getTuitionFee(program?.tuition_fee) }}
                </p>
              </div>
            </div>
          </div>
          <div class="space-y-4">
            <ChooseSchedule :program="program" />
            <div class="flex items-center space-x-[-12px]">
              <div
                v-for="(img, index) in mentorImages"
                :key="index"
                class="w-10 h-10 rounded-full border-2 border-white overflow-hidden"
              >
                <img
                  :src="img"
                  alt="Mentor"
                  class="w-full h-full object-cover"
                />
              </div>
              <div
                class="w-10 h-10 rounded-full border-2 border-white bg-white flex items-center justify-center text-sm font-medium text-blue-700"
              >
                +48
              </div>
            </div>
          </div>
        </div>

        <div class="lg:grid lg:grid-cols-12 gap-16">
          <ul class="hidden md:block md:col-span-3">
            <li
              v-for="item in sidebarItems"
              :key="item + 'program-detail-side-bar-item-key'"
              class="h-9 flex items-center gap-3 hover:bg-gray-200 cursor-pointer"
              @click="() => selectSidebar(item)"
            >
              <div
                :class="[
                  'w-1 h-full rounded-r-lg',
                  activeSidebar === item ? 'bg-primary-500' : '',
                ]"
              ></div>
              <p
                :class="[
                  'text-sm',
                  activeSidebar === item
                    ? 'font-bold text-primary-500'
                    : 'font-semibold text-[#6B7280]',
                ]"
              >
                {{ item }}
              </p>
            </li>
          </ul>

          <div class="col-span-7 md:col-span-9">
            <div class="md:mt-5 mt-10 space-y-16">
              <!-- Program -->
              <TitleSection title="ABOUT THIS PROGRAM" id="Program">
                <div
                  v-html="program?.about"
                  class="ql-editor editor-text text-gray-800 text-base leading-[1.8] prose prose-neutral max-w-none"
                ></div>
              </TitleSection>

              <!-- Key Information -->
              <TitleSection
                showDivider
                title="KEY INFORMATION"
                id="Key information"
              >
                <div class="flex flex-col sm:flex-col md:flex-row gap-6">
                  <div class="flex-1 grid grid-cols-12 gap-x-4 gap-y-6">
                    <div class="col-span-12 sm:col-span-6 md:col-span-4">
                      <KeyInfoCard
                        iconClass="text-xl"
                        label="Degree"
                        icon="pi-graduation-cap"
                        :value="getAttributeData('degree')"
                      />
                    </div>

                    <div class="col-span-12 sm:col-span-6 md:col-span-4">
                      <KeyInfoCard
                        iconClass="text-xl"
                        label="Language"
                        icon="pi-comment"
                        :value="getAttributeData('language')"
                      />
                    </div>

                    <div class="col-span-12 sm:col-span-6 md:col-span-4">
                      <KeyInfoCard
                        iconClass="text-xl"
                        label="Location"
                        icon="pi-map-marker"
                        :value="
                          getCitiesName(program?.university?.cities) ||
                          'Not provided'
                        "
                      />
                    </div>

                    <div class="col-span-12 sm:col-span-6 md:col-span-4">
                      <KeyInfoCard
                        iconClass="text-xl"
                        label="Intake Start Date"
                        icon="pi-calendar"
                        :value="
                          program?.intakes?.data?.length > 0
                            ? formatDate(nearestStartDate)
                            : 'Data not available'
                        "
                      />
                    </div>

                    <div class="col-span-12 sm:col-span-6 md:col-span-4">
                      <KeyInfoCard
                        iconClass="text-xl"
                        label="Study Format"
                        icon="pi-book"
                        :value="getAttributeData('study_format')"
                      />
                    </div>

                    <div class="col-span-12 sm:col-span-6 md:col-span-4">
                      <KeyInfoCard
                        iconClass="text-xl"
                        label="Duration"
                        icon="pi-hourglass"
                        :value="getAttributeData('duration')"
                      />
                    </div>

                    <div class="col-span-12 sm:col-span-6 md:col-span-4">
                      <KeyInfoCard
                        iconClass="text-xl"
                        label="Program Highlight"
                        icon="pi-star"
                        :value="
                          getAttributeData('program_highlight') == 'N/A'
                            ? 'Standard Program'
                            : getAttributeData('program_highlight')
                        "
                      />
                    </div>

                    <div class="col-span-12 sm:col-span-6 md:col-span-4">
                      <KeyInfoCard
                        iconClass="text-xl"
                        label="Total Semester"
                        icon="pi-calendar"
                        :value="getAttributeData('total_semester')"
                      />
                    </div>

                    <div class="col-span-12 sm:col-span-6 md:col-span-4">
                      <KeyInfoCard
                        iconClass="text-xl"
                        label="Total Tuition Fees"
                        icon="pi-wallet"
                        :value="
                          getTuitionFee(program?.tuition_fee) +
                          ' ' +
                          formatPaymentType(
                            program?.tuition_fee?.recurring_payment_type,
                            program?.tuition_fee,
                          )
                        "
                      />
                    </div>
                  </div>

                  <div
                    class="w-full md:w-[300px] p-6 rounded-xl shadow-md bg-white flex items-center"
                  >
                    <div class="space-y-6 w-full">
                      <div class="flex flex-col items-center gap-2 text-center">
                        <i class="pi pi-calendar text-slate-900 text-3xl"></i>
                        <p class="text-sm text-slate-900 font-normal">
                          Application deadline
                        </p>
                        <p class="text-[18px] text-red-500 font-bold">
                          {{
                            program?.intakes?.data?.length > 0
                              ? formatDate(new Date(nearestEndDate))
                              : "Data not available"
                          }}
                        </p>
                      </div>
                      <div class="flex justify-center">
                        <ChooseSchedule
                          :program="program"
                          label="Request information"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </TitleSection>

              <!-- About university -->
              <TitleSection title="ABOUT UNIVERSITY" id="University">
                <div class="flex flex-col sm:flex-row sm:flex-nowrap gap-6">
                  <Avatar
                    v-if="getUniversityLogo(program?.university?.media)"
                    :image="getUniversityLogo(program?.university?.media)"
                    shape="circle"
                    size="normal"
                    class="!w-24 !h-24 rounded-full shrink-0"
                  />
                  <Avatar
                    v-else
                    :label="program?.university?.name?.at(0)"
                    shape="circle"
                    size="normal"
                    class="!w-24 !h-24 rounded-full shrink-0"
                  />

                  <div class="space-y-10">
                    <div class="">
                      <NuxtLink
                        :to="`/universities/${program?.university?.slug}`"
                        class="text-[18px] font-bold text-[#4B5563] mb-2 hover:underline"
                      >
                        {{ program?.university?.name }}
                      </NuxtLink>
                      <p class="text-gray-800 text-sm">
                        {{ program?.university?.university_address?.address_1 }}
                        {{ program?.university?.university_address?.country }}
                      </p>
                    </div>

                    <div class="flex flex-col lg:flex-row flex-nowrap gap-4">
                      <KeyInfoCard
                        rootClass="rounded-none"
                        iconClass="text-xl"
                        :label="'Asia Ranking ' + new Date().getFullYear()"
                        icon="pi-building"
                        :value="
                          program?.university?.ranking?.name +
                          ' ' +
                          program?.university?.ranking?.number
                        "
                      />

                      <Divider v-if="!isMobile" layout="vertical" />

                      <KeyInfoCard
                        rootClass="rounded-none"
                        iconClass="text-xl"
                        label="Programs"
                        icon="pi-book"
                        :value="program?.university?.program_count"
                      />

                      <Divider v-if="!isMobile" layout="vertical" />

                      <KeyInfoCard
                        rootClass="rounded-none"
                        iconClass="text-xl"
                        label="Students"
                        icon="pi-graduation-cap"
                        :value="
                          numberFormatter.format(
                            program?.university?.attributes?.data?.find(
                              (att) => att.key === 'student_count',
                            )?.value || 0,
                          )
                        "
                      />

                      <!-- <Divider layout="vertical" />

                      <div class="flex gap-4 items-start flex-nowrap">
                        <p class="text-[28px] text-primary-500 font-bold -mt-2">
                          4.5
                        </p>

                        <div class="space-y-1.5">
                          <Rating />
                          <p class="text-[18px] font-bold text-primary-500">
                            Excellent
                          </p>
                        </div>
                      </div> -->
                    </div>

                    <div class="">
                      <div
                        :class="[
                          'overflow-hidden relative',
                          expandUniAbout ? 'h-auto' : 'h-24',
                        ]"
                      >
                        <div
                          v-if="!expandUniAbout"
                          class="absolute bottom-0 h-20 w-full z-10"
                          style="
                            background: linear-gradient(
                              to top,
                              #f9fafb,
                              transparent
                            );
                          "
                        ></div>

                        <div
                          v-html="program?.university?.about"
                          class="ql-editor editor-text text-gray-800 text-sm leading-[1.6] prose prose-neutral max-w-none"
                        ></div>
                      </div>
                      <div class="flex items-center justify-center gap-2">
                        <div
                          class="flex items-center gap-2 cursor-pointer"
                          @click="expandUniAbout = !expandUniAbout"
                        >
                          <p class="text-sm font-bold text-primary-600">
                            See {{ expandUniAbout ? "less" : "more" }}
                          </p>
                          <i
                            :class="[
                              'pi',
                              expandUniAbout ? 'pi-angle-up' : 'pi-angle-down',
                            ]"
                          ></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TitleSection>

              <!-- Program structure -->
              <TitleSection
                title="PROGRAM STRUCTURE"
                id="Program structure"
                v-if="program?.program_structures?.length > 0"
              >
                <Tabs :value="program?.program_structures?.at(0)?.step">
                  <TabList>
                    <Tab
                      :key="index + programStructure?.step + 'program-tab'"
                      v-for="(
                        programStructure, index
                      ) in program?.program_structures"
                      :value="programStructure?.step"
                      >{{ programStructure?.step }}</Tab
                    >
                  </TabList>
                  <TabPanels>
                    <TabPanel
                      class="py-4"
                      :key="
                        index + programStructure?.step + 'program-tab-panel'
                      "
                      v-for="(
                        programStructure, index
                      ) in program?.program_structures"
                      :value="programStructure?.step"
                    >
                      <div
                        v-html="programStructure?.detail"
                        class="ql-editor editor-text text-gray-800 text-base leading-relaxed"
                      ></div>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </TitleSection>

              <!-- Tuition fees -->
              <TitleSection showDivider title="TUITION FEES" id="Tuition fees">
                <div class="flex flex-col md:flex-row gap-8">
                  <div class="space-y-8">
                    <div
                      class="col-span-4 w-full flex gap-4 items-start flex-nowrap place-self-start"
                    >
                      <i
                        :class="[
                          'text-md pi text-xl text-[#4B5563] mt-1',
                          'pi-wallet',
                        ]"
                      ></i>

                      <div class="space-y-1.5">
                        <p class="text-sm font-normal text-[#4B5563]">
                          Tuition fees
                        </p>
                        <p class="text-[18px] font-bold text-primary-500">
                          <span>{{ getTuitionFee(program?.tuition_fee) }}</span>
                          <span class="text-xs font-semibold">
                            {{
                              formatPaymentType(
                                program?.tuition_fee?.recurring_payment_type,
                                program?.tuition_fee,
                              )
                            }}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div
                      class="col-span-4 w-full flex gap-4 items-start flex-nowrap place-self-start"
                    >
                      <i
                        :class="[
                          'text-md pi text-xl text-[#4B5563] mt-1',
                          'pi-receipt',
                        ]"
                      ></i>

                      <div class="space-y-1.5">
                        <p class="text-sm font-normal text-[#4B5563]">
                          Application fees
                        </p>
                        <p class="text-[18px] font-bold text-primary-500">
                          {{ getApplicationFee(program?.tuition_fee) }}
                        </p>
                      </div>
                    </div>
                    <div
                      class="text-blue-600 flex items-center gap-3 flex-nowrap"
                    >
                      <i class="pi pi-info-circle text-2xl"></i>
                      <p class="font-semibold">
                        Application fee cannot be refunded.
                      </p>
                    </div>
                  </div>

                  <div
                    class="md:w-[495px] w-full p-6 rounded-xl shadow-md bg-white flex items-start gap-10"
                  >
                    <i class="pi pi-star text-[#62D4BD] text-[40px]"></i>
                    <div class="space-y-4">
                      <p class="text-xl font-bold text-primary-500">
                        Apply for Scholarships with UniLinks
                      </p>

                      <p class="text-sm font-normal text-slate-900">
                        Find the right scholarships for your goals -- and let
                        UniLinks walk you through the process, step by step
                      </p>

                      <ChooseSchedule
                        :program="program"
                        label="Talk to our Student Ambassadors"
                      />
                    </div>
                  </div>
                </div>
              </TitleSection>

              <TitleSection title="ADMISSIONS PROCESS" id="Admissions">
                <div class="h-48">
                  <img
                    src="~assets/img/progress-bar.png"
                    alt="Progress Bar"
                    class="h-full object-contain"
                  />
                </div>

                <div
                  v-if="getAttributeData('admission_process')"
                  class="ql-editor editor-text text-gray-800 text-base leading-[1.8] prose prose-neutral max-w-none"
                  :class="[
                    getAttributeData('admission_process')
                      ? 'text-gray-800 text-base'
                      : 'text-gray-800 text-base',
                  ]"
                  v-html="getAttributeData('admission_process')"
                ></div>
              </TitleSection>

              <!-- Possible career pathway -->
              <TitleSection
                title="Possible Career Pathway"
                id="Possible Career Pathway"
              >
                <div
                  v-if="
                    getAttributeData('possible_career_pathway') &&
                    !getAttributeData('possible_career_pathway')
                      .toUpperCase()
                      .includes('N/A')
                  "
                  class="ql-editor editor-text text-gray-800 text-base leading-[1.8] prose prose-neutral max-w-none"
                  v-html="getAttributeData('possible_career_pathway')"
                ></div>
                <p v-else class="text-gray-800 text-base leading-[1.8]">
                  Career information coming soon.
                </p>
              </TitleSection>

              <!-- STUDENT's TESTIMONIALS -->
              <TitleSection
                title="STUDENT's TESTIMONIALS"
                id="Student's testimonials"
              >
                <div
                  class="flex justify-between gap-8"
                  v-if="program?.university?.testimonials?.data?.length > 0"
                >
                  <CustomCarousel
                    :slidesPerView="
                      program?.university?.testimonials?.data?.length > 2
                        ? slidesPerView
                        : 1
                    "
                    :data="program?.university?.testimonials?.data"
                  >
                    <template #default="{ data: reviewer }">
                      <!-- :key="reviewer?.id + 'review-card-key'"
                        v-for="reviewer in program?.university?.testimonials
                          ?.data" -->
                      <ReviewCard
                        icon="pi-book"
                        :profileUrl="getTestimonialImage(reviewer.media)"
                        :content="reviewer?.content"
                        :name="reviewer?.name"
                        :position="reviewer?.position"
                      />
                    </template>
                  </CustomCarousel>
                </div>
                <p v-else class="text-sm italic text-muted">
                  No testimonials available just yet — stay tuned!
                </p>
              </TitleSection>

              <!-- FAQs -->
              <TitleSection title="FAQs" id="F&Qs">
                <div class="pt-4">
                  <div
                    v-if="program?.university?.faqs?.data?.length > 0"
                    v-for="(faq, index) in program?.university?.faqs?.data"
                    :key="faq.id + 'faq-key'"
                    class="grid grid-cols-1 sm:grid-cols-9 gap-y-4 gap-x-6 text-sm text-gray-800"
                  >
                    <p class="col-span-1">Question :</p>
                    <p class="col-span-8 font-semibold">
                      {{ faq?.question }}
                    </p>

                    <p class="col-span-1">Answer :</p>
                    <p class="col-span-8 font-semibold">
                      {{ faq?.answer }}
                    </p>

                    <div
                      class="col-span-1"
                      v-if="index + 1 < program?.university?.faqs?.data?.length"
                    />
                    <Divider
                      class="col-span-8"
                      v-if="index + 1 < program?.university?.faqs?.data?.length"
                    />
                  </div>
                  <p v-else class="text-sm italic text-muted">
                    Not provided yet
                  </p>
                </div>
              </TitleSection>
              <br />
              <!--  -->
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  </NuxtLayout>
</template>
